import React, { useCallback, useEffect, useMemo, useState, createContext, useContext } from 'react';
import { Spin } from 'antd';
import styled from 'styled-components';

import {
  useGetCriticalEvent,
  useCreateCriticalEvent,
  useUpdateCriticalEvent,
} from '@src/services/query/applang/queryHooks';
import CriticalEventForm, { type CriticalEventFormResult } from '@src/features/CriticalEventForm';
import { getEmptyRuleGroupInAPIForm } from '@src/shared/utils/eventBuilder';
import { useCommonInfoStore } from '@src/shared/store/common';

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
`;

// Create context for interval
interface CriticalEventIntervalContextValue {
  interval?: [string, string];
}

const CriticalEventIntervalContext = createContext<CriticalEventIntervalContextValue>({});

// Hook to use the interval context
// Returns undefined if used outside of CriticalEventFormContainer
export const useCriticalEventInterval = () => {
  const context = useContext(CriticalEventIntervalContext);
  return context?.interval;
};

interface CriticalEventFormContainerProps {
  /** ID of the critical event to edit. If provided without initialValue, will fetch from API */
  id?: string;
  /** Initial values for the form. Used for both create and edit modes */
  initialValue?: CriticalEvent | Partial<CriticalEvent>;
  /** Callback when form changes */
  onChange?: (event: CriticalEventFormResult) => void;
  /** Callback when form is saved */
  onSave?: (event?: CriticalEventFormResult) => void;
  /** Callback when form is cancelled */
  onCancel?: (event?: Partial<CriticalEvent>) => void;
  /** Custom text for cancel button */
  cancelText?: string;
  interval?: [string, string];
}

const CriticalEventFormContainer: React.FC<CriticalEventFormContainerProps> = ({
  id,
  initialValue,
  onChange,
  onSave,
  onCancel,
  cancelText,
  interval,
}) => {
  const defaultInterval = useCommonInfoStore((state) => state.defaultInterval);
  const [localInitialValue, setLocalInitialValue] = useState<
    CriticalEvent | Partial<CriticalEvent> | undefined
  >();
  // Only fetch if id is provided and no initialValue is given
  const shouldFetch = useMemo(() => {
    return !!id && !initialValue;
  }, [id, initialValue]);

  const {
    data: fetchedEvent,
    isFetching: isFetchingEvent,
    error,
  } = useGetCriticalEvent(shouldFetch ? id : undefined);
  const { mutateAsync: createEvent, isLoading: isCreating } = useCreateCriticalEvent();
  const { mutateAsync: updateEvent, isLoading: isUpdating } = useUpdateCriticalEvent();

  // Update local initial value when data is fetched or initialValue changes
  useEffect(() => {
    if (isFetchingEvent) return;
    if (initialValue) {
      setLocalInitialValue(initialValue);
    } else if (fetchedEvent) {
      setLocalInitialValue(fetchedEvent);
    } else {
      // initial value is empty, set the empty event
      const emptyEvent: Partial<CriticalEvent> = {
        name: '',
        rules: [[getEmptyRuleGroupInAPIForm()]],
        is_milestone: false,
      };
      setLocalInitialValue(emptyEvent);
    }
  }, [initialValue, fetchedEvent, isFetchingEvent]);

  // Handle save action
  const handleSave = useCallback(
    (event?: CriticalEventFormResult) => {
      if (!event) return;

      // Determine if we're creating or updating based on ce_id
      const isEditing = !!(event as CriticalEvent).ce_id || !!id;

      // Execute async operation without returning the promise
      void (async () => {
        try {
          if (isEditing) {
            // Edit mode: use ce_id from event or the provided id
            const ceId = (event as CriticalEvent).ce_id || id;
            if (!ceId) {
              console.error('No ce_id available for update');
              return;
            }

            // Wait for update and refetch to complete
            const response = await updateEvent({
              ceId,
              data: {
                name: event.name,
                rules: event.rules,
                is_milestone: event.is_milestone,
              },
            });

            // Call onSave after refetch is complete
            onSave?.(response?.critical_event);
          } else {
            // Create mode - wait for creation and refetch to complete
            const response = await createEvent({
              name: event.name,
              rules: event.rules,
              is_milestone: event.is_milestone,
            });

            // Call onSave after refetch is complete
            onSave?.(response?.critical_event);
          }
        } catch (error) {
          // Error handling is done in the mutation hooks
          console.error('Save operation failed:', error);
        }
      })();
    },
    [id, createEvent, updateEvent, onSave],
  );

  // Handle cancel action
  const handleCancel = useCallback(
    (event?: Partial<CriticalEvent>) => {
      onCancel?.(event);
    },
    [onCancel],
  );

  // Show loading state when fetching or saving
  if (shouldFetch && isFetchingEvent) {
    return (
      <LoadingWrapper>
        <Spin size="large" tip="Loading event details..." />
      </LoadingWrapper>
    );
  }

  // Show error state (error messages are already shown via the hook)
  if (shouldFetch && error) {
    return (
      <LoadingWrapper>
        <div style={{ color: '#ff4d4f', textAlign: 'center' }}>
          Failed to load critical event. Please try again.
        </div>
      </LoadingWrapper>
    );
  }

  // Render the form with the resolved initial value
  return (
    <CriticalEventIntervalContext.Provider value={{ interval }}>
      <CriticalEventForm
        initialEvent={localInitialValue}
        onChange={onChange}
        onSave={handleSave}
        onCancel={handleCancel}
        cancelText={cancelText}
        isLoading={isCreating || isUpdating}
      />
    </CriticalEventIntervalContext.Provider>
  );
};

export default CriticalEventFormContainer;
